/**
 * S-91 第2弾: SSO 再サインインの省略判定。
 *
 * `#sso_token` 付きで遷移してきても、遷移先オリジンに**同一人物・同一 claims** の
 * 永続セッションが既にあるなら `signInWithCustomToken`（identitytoolkit 2 往復 =
 * boot ローダー区間の主往復）を省略できる。
 *
 * - uid だけでなくハンドオーバー関連 claims（role / tenantId / features / readOnly /
 *   impersonatedBy）の一致も要求する。特に S-79 の readOnly（impersonation 由来の
 *   書込禁止）は、既存セッションとの差分を無視すると制約が実効化しないため厳密比較する
 * - Custom Token の署名検証はしない — ここでの decode は「省略できるか」の比較にのみ
 *   使い、省略しない場合の認証は従来どおり `signInWithCustomToken`（サーバ検証）が行う。
 *   偽造 token で得られるのは最悪でも「既存の自分のセッションを使い続ける」だけで、
 *   権限昇格には使えない
 */
/** ハンドオーバーで比較する claim キー（functions の buildHandoffClaims と対）。 */
const HANDOFF_CLAIM_KEYS = [
    'role',
    'tenantId',
    'features',
    'readOnly',
    'impersonatedBy',
];
/**
 * Firebase Custom Token（base64url JWT）の payload から uid と handoff claims を
 * 取り出す純関数。形状が不正なら null。
 */
export function decodeCustomTokenPayload(token) {
    const segment = token.split('.')[1];
    if (!segment)
        return null;
    try {
        const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
        const bin = atob(padded);
        const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
        const payload = JSON.parse(new TextDecoder().decode(bytes));
        if (typeof payload !== 'object' || payload === null)
            return null;
        const p = payload;
        const claims = p.claims;
        return {
            uid: typeof p.uid === 'string' && p.uid.length > 0 ? p.uid : null,
            claims: typeof claims === 'object' && claims !== null && !Array.isArray(claims)
                ? claims
                : {},
        };
    }
    catch {
        return null;
    }
}
/**
 * 値の等価キー。`undefined`（キー不在）と `null` を区別し、配列（features）は
 * 順序差を吸収するためソートして比較する。
 */
function claimKeyOf(value) {
    if (value === undefined)
        return '__absent__';
    if (Array.isArray(value))
        return JSON.stringify([...value].sort());
    return JSON.stringify(value);
}
/**
 * トークンの handoff claims と現セッションの ID token claims が「同一人物・同一権限」
 * とみなせるか。HANDOFF_CLAIM_KEYS の全キーが一致した場合のみ true。
 */
export function canReuseExistingSession(tokenClaims, currentClaims) {
    return HANDOFF_CLAIM_KEYS.every((key) => claimKeyOf(tokenClaims[key]) === claimKeyOf(currentClaims[key]));
}
//# sourceMappingURL=ssoReuse.js.map